import {
  type Component,
  createSignal,
  createResource,
  createEffect,
  For,
  Show,
  createMemo,
} from "solid-js"
import { useParams, useNavigate } from "@solidjs/router"
import type {
  WorkplaceId,
  WorkerId,
  MonthString,
  DateString,
  WorkType,
} from "@eckersdorf/api"
import { ScheduleGrid } from "../components/ScheduleGrid"
import { HeaderSlot } from "../components/HeaderSlot"
import { StatsPanel } from "../components/StatsPanel"
import {
  getWorkplaces,
  getWorkers,
  getSchedules,
  saveSchedules,
} from "../lib/api"
import { printSchedule } from "../lib/print-utils"
import { useAuth } from "../lib/auth"

const POLISH_MONTHS = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
]

export const SchedulePage: Component = () => {
  const { user } = useAuth()
  const params = useParams()
  const navigate = useNavigate()

  const selectedWorkplace = createMemo(() => params['workplaceId'] as WorkplaceId | undefined)
  const selectedMonth = createMemo(() => (params['month'] as MonthString) || (new Date().toISOString().slice(0, 7) as MonthString))

  const [scheduleData, setScheduleData] = createSignal<
    Record<string, Record<string, Record<string, WorkType | null>>>
  >({})
  const [dirty, setDirty] = createSignal(false)
  const [saving, setSaving] = createSignal(false)
  const [showStats, setShowStats] = createSignal(false)

  // Fetch workplaces
  const [workplaces] = createResource(
    user,
    async (u) => {
      if (!u) return []
      return getWorkplaces()
    },
    { initialValue: [] }
  )

  // Fetch workers for selected workplace
  const [workers] = createResource(
    () => ({ user: user(), workplaceId: selectedWorkplace() }),
    async ({ user: u, workplaceId }) => {
      if (!u || !workplaceId) return []
      return getWorkers(workplaceId)
    },
    { initialValue: [] }
  )

  // Fetch schedules
  const [schedules, { refetch: refetchSchedules }] = createResource(
    () => ({
      user: user(),
      workplaceId: selectedWorkplace(),
      month: selectedMonth(),
    }),
    async ({ user: u, workplaceId, month }) => {
      if (!u || !workplaceId) return null
      return getSchedules(workplaceId, month)
    }
  )

  // Update local schedule data when fetched
  createEffect(() => {
    const s = schedules()
    if (s !== null && s !== undefined) {
      const data: Record<string, Record<string, Record<string, WorkType | null>>> = {}
      for (const schedule of s.schedules) {
        if (data[schedule.workerId] === undefined) {
          data[schedule.workerId] = {}
        }
        const workerData = data[schedule.workerId]
        if (workerData !== undefined) {
          workerData[schedule.date] = { ...schedule.scheduleJson }
        }
      }
      setScheduleData(data)
      setDirty(false)
    }
  })

  const handlePrint = () => {
    const wp = selectedWorkplace()
    const m = selectedMonth()
    const currentWorkers = workers()
    const currentSchedules = scheduleData()
    const workplace = workplaces().find(w => w.id === wp)
    
    if (wp && m && currentWorkers && workplace) {
      printSchedule({
        workplaceName: workplace.name,
        month: m,
        workers: currentWorkers,
        schedules: currentSchedules,
      })
    }
  }

  const handleCellClick = (
    workerId: WorkerId,
    date: DateString,
    hour: number
  ): void => {
    const hourStr = hour.toString().padStart(2, "0")
    setScheduleData((prev) => {
      const workerData = prev[workerId] ?? {}
      const dayData = workerData[date] ?? {}
      const current = dayData[hourStr] ?? null

      // Cycle: null -> seller -> butcher -> null
      let next: WorkType | null
      switch (current) {
        case null:
          next = "seller"
          break
        case "seller":
          next = "butcher"
          break
        case "butcher":
          next = null
          break
       case "holiday":
       case "vacation":
       case "sickLeave":
        case "wolne":
         next = current
         break
       default: {
          const _exhaustive: never = current
          next = null
        }
      }

      return {
        ...prev,
        [workerId]: {
          ...workerData,
          [date]: {
            ...dayData,
            [hourStr]: next,
          },
        },
      }
    })
    setDirty(true)
  }

  const handleCellDoubleClick = (
    workerId: WorkerId,
    date: DateString,
    hour: number
  ): void => {
    const worker = workers().find((w) => w.id === workerId)
    if (worker === undefined) return

    const hourStr = hour.toString().padStart(2, "0")
    const hoursToFill = Math.min(worker.workingHours, 21 - hour + 1)

    setScheduleData((prev) => {
      // Read current state from the LATEST prev (after single click updated it)
      const currentType = prev[workerId]?.[date]?.[hourStr] ?? null

     // Holiday/vacation/sickLeave cells: do nothing on double-click
      if (currentType === "holiday" || currentType === "vacation" || currentType === "sickLeave" || currentType === "wolne") {
       return prev
     }

      // The single click already cycled the state:
      // - Was empty -> now seller -> fill with seller
      // - Was seller -> now butcher -> fill with butcher
      // - Was butcher -> now null -> fill with null (clear)
      const fillType = currentType

      const workerData = prev[workerId] ?? {}
      const dayData = { ...(workerData[date] ?? {}) }

      for (let h = hour; h < hour + hoursToFill; h++) {
        const hStr = h.toString().padStart(2, "0")
        dayData[hStr] = fillType
      }

      return {
        ...prev,
        [workerId]: {
          ...workerData,
          [date]: dayData,
        },
      }
    })
    setDirty(true)
  }

  const handleSetCell = (
    workerId: WorkerId,
    date: DateString,
    hour: number,
    type: WorkType | null
  ): void => {
    const hourStr = hour.toString().padStart(2, "0")
    setScheduleData((prev) => {
      const workerData = prev[workerId] ?? {}
      const dayData = workerData[date] ?? {}
      return {
        ...prev,
        [workerId]: {
          ...workerData,
          [date]: {
            ...dayData,
            [hourStr]: type,
          },
        },
      }
    })
    setDirty(true)
  }

  const handleSetFullDay = (
    workerId: WorkerId,
    date: DateString,
    type: WorkType
  ): void => {
    setScheduleData((prev) => {
      const workerData = prev[workerId] ?? {}
      const dayData = { ...(workerData[date] ?? {}) }

      // Hours 6-21
      for (let h = 6; h <= 21; h++) {
        const hStr = h.toString().padStart(2, "0")
        dayData[hStr] = type
      }

      return {
        ...prev,
        [workerId]: {
          ...workerData,
          [date]: dayData,
        },
      }
    })
    setDirty(true)
  }

  const handleSetHoliday = (date: DateString): void => {
    setScheduleData((prev) => {
      const currentWorkers = workers()
      
      // Check if all are holiday (check hour 12 for all workers)
      let allHoliday = true
      if (currentWorkers.length === 0) allHoliday = false
      
      for (const worker of currentWorkers) {
        const type = prev[worker.id]?.[date]?.["12"]
        if (type !== "holiday") {
          allHoliday = false
          break
        }
      }

      const next = { ...prev }
      const targetType = allHoliday ? null : "holiday"

      for (const worker of currentWorkers) {
        const workerData = next[worker.id] ?? {}
        const dayData = { ...(workerData[date] ?? {}) }
        
        for (let h = 6; h <= 21; h++) {
          const hStr = h.toString().padStart(2, "0")
          dayData[hStr] = targetType
        }
        
        next[worker.id] = {
          ...workerData,
          [date]: dayData,
        }
      }
      return next
    })
    setDirty(true)
  }

  const handleClearDay = (workerId: WorkerId, date: DateString): void => {
    setScheduleData((prev) => {
      const workerData = prev[workerId] ?? {}
      const dayData = { ...(workerData[date] ?? {}) }

      for (let h = 6; h <= 21; h++) {
        const hStr = h.toString().padStart(2, "0")
        dayData[hStr] = null
      }

      return {
        ...prev,
        [workerId]: {
          ...workerData,
          [date]: dayData,
        },
      }
    })
    setDirty(true)
  }

  const handleClearDayAll = (date: DateString): void => {
    setScheduleData((prev) => {
      const currentWorkers = workers()
      const next = { ...prev }

      for (const worker of currentWorkers) {
        const workerData = next[worker.id] ?? {}
        const dayData = { ...(workerData[date] ?? {}) }

        for (let h = 6; h <= 21; h++) {
          const hStr = h.toString().padStart(2, "0")
          dayData[hStr] = null
        }

        next[worker.id] = {
          ...workerData,
          [date]: dayData,
        }
      }
      return next
    })
    setDirty(true)
  }

  const handleSetShift = (
    workerId: WorkerId,
    date: DateString,
    startHour: number,
    hours: number,
    type: WorkType
  ): void => {
    setScheduleData((prev) => {
      const workerData = prev[workerId] ?? {}
      const dayData = { ...(workerData[date] ?? {}) }

      for (let h = startHour; h < startHour + hours && h <= 21; h++) {
        const hStr = h.toString().padStart(2, "0")
        dayData[hStr] = type
      }

      return {
        ...prev,
        [workerId]: {
          ...workerData,
          [date]: dayData,
        },
      }
    })
    setDirty(true)
  }

  const availableYears = () => {
    const currentYear = new Date().getFullYear()
    return [currentYear - 1, currentYear, currentYear + 1]
  }

  const handleSave = async (): Promise<void> => {
    const workplaceId = selectedWorkplace()
    const month = selectedMonth()
    if (workplaceId === undefined) return

    setSaving(true)
    try {
      const data = scheduleData()
      const schedulesToSave: Array<{
        workerId: WorkerId
        date: DateString
        scheduleJson: Record<string, WorkType | null>
        hoursTotal: number
      }> = []

      for (const [workerId, workerData] of Object.entries(data)) {
        for (const [date, dayData] of Object.entries(workerData)) {
          const hoursTotal = Object.values(dayData).filter(
            (v) => v !== null && v !== "holiday" && v !== "vacation" && v !== "sickLeave" && v !== "wolne"
          ).length
          schedulesToSave.push({
            workerId: workerId as WorkerId,
            date: date as DateString,
            scheduleJson: dayData,
            hoursTotal,
         })
       }
      }

      await saveSchedules({
        workplaceId,
        month,
        schedules: schedulesToSave,
      })

      setDirty(false)
    } catch (error) {
      console.error("Failed to save:", error)
      alert("Błąd podczas zapisywania")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      {/* Toolbar */}
      <HeaderSlot>
        <div class="flex items-center gap-2 flex-nowrap">
          <select
          value={selectedWorkplace() ?? ""}
          onChange={(e) => {
            const val = e.currentTarget.value
            if (val) {
              navigate(`/schedule/${val}/${selectedMonth()}`)
            } else {
              navigate(`/schedule`)
            }
          }}
          class="px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <option value="">-- Wybierz sklep --</option>
          <For each={workplaces()}>
            {(wp) => <option value={wp.id}>{wp.name}</option>}
          </For>
          </select>

          <select
            value={selectedMonth().split("-")[1]}
            disabled={selectedWorkplace() === undefined}
            onChange={(e) => {
              const [year] = selectedMonth().split("-")
              const month = e.currentTarget.value
              const wp = selectedWorkplace()
              if (wp) navigate(`/schedule/${wp}/${year}-${month}`)
            }}
            class="px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <For each={POLISH_MONTHS}>
              {(name, i) => (
                <option value={(i() + 1).toString().padStart(2, "0")}>
                  {name}
                </option>
              )}
            </For>
          </select>

          <select
            value={selectedMonth().split("-")[0]}
            disabled={selectedWorkplace() === undefined}
            onChange={(e) => {
              const [, month] = selectedMonth().split("-")
              const year = e.currentTarget.value
              const wp = selectedWorkplace()
              if (wp) navigate(`/schedule/${wp}/${year}-${month}`)
            }}
            class="px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <For each={availableYears()}>
              {(year) => <option value={year.toString()}>{year}</option>}
            </For>
          </select>

          <button
          onClick={() => setShowStats(true)}
          class="px-3 py-1.5 rounded-md font-medium text-gray-700 bg-gray-100 border border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors text-sm"
        >
          Podsumowanie
          </button>

          <button
          onClick={handlePrint}
          class="px-3 py-1.5 rounded-md font-medium text-gray-700 bg-gray-100 border border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors text-sm"
        >
          Drukuj
          </button>

          <Show when={dirty()}>
            <button
            onClick={() => {
              void handleSave()
            }}
            disabled={saving()}
            class={`px-3 py-1.5 rounded-md font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors text-sm ${saving() ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {saving() ? "Zapisywanie..." : "Zapisz"}
            </button>
          </Show>

          <Show when={dirty()}>
            <button
            onClick={() => {
              void refetchSchedules()
            }}
            class="px-3 py-1.5 rounded-md font-medium text-gray-700 bg-gray-100 border border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors text-sm"
          >
            Anuluj
            </button>
          </Show>
        </div>
      </HeaderSlot>

      {/* Content */}
      <Show
        when={selectedWorkplace() !== undefined}
        fallback={
          <div class="text-center py-12 text-gray-500  rounded-lg border border-gray-200 border-dashed">
            <p>Wybierz sklep, aby wyświetlić harmonogram.</p>
          </div>
        }
      >
        <Show when={workers.loading || schedules.loading}>
          <div class="flex justify-center p-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </Show>

        <Show when={workers.error !== undefined}>
          <div class="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-4">
            Błąd: {String(workers.error)}
          </div>
        </Show>

        <Show
          when={
            (workers()?.length ?? 0) > 0 &&
            schedules.loading === false &&
            workers.loading === false
          }
        >
          <div class=" rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col flex-1">
            <div class="flex-1 overflow-auto">
              <ScheduleGrid
                workers={workers()}
                month={selectedMonth()}
                schedules={scheduleData()}
                onCellClick={handleCellClick}
                onCellDoubleClick={handleCellDoubleClick}
                onSetCell={handleSetCell}
                onSetFullDay={handleSetFullDay}
                onSetShift={handleSetShift}
                onSetHoliday={handleSetHoliday}
                onClearDay={handleClearDay}
                onClearDayAll={handleClearDayAll}
                readonly={user()?.role === "worker"}
              />
            </div>
          </div>
        </Show>

        <Show when={(workers()?.length ?? 0) === 0 && workers.loading === false}>
          <div class="text-center py-12 text-gray-500  rounded-lg border border-gray-200">
            <p>Brak pracowników w tym sklepie.</p>
          </div>
        </Show>
      </Show>

      <Show when={showStats()}>
        <StatsPanel
          workers={workers()}
          month={selectedMonth()}
          schedules={scheduleData()}
          onClose={() => setShowStats(false)}
        />
      </Show>
    </>
  )
}
